import {createFileRoute, redirect} from "@tanstack/react-router";
import {z as zod} from "zod";
import {getDataSourcesForFrontend} from "~/domainModels/dag/dataSources";
import {OnboardingJourneyStep, onboardingJourneyZodValidator} from "~/domainModels/onboardingJourney";
import {jsonZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {DAG_ConnectionAttemptStatus} from "~/domainModels/dag/dataSourceConnectionAttempt";
import {dataSourceConnectionStrategyDetailsMap} from "~/domainModels/dag/dataSourceConnectionStrategy";
import {generateUuid, getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {getAccessibleWorkspacesWithCaching} from "~/utilities/queries";
import {throwRedirectToConfigConnectDataSourcesWhenPresent} from "~/workspaceConfig/configOttosyncRedirects";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottosync/onboarding/connect-data-sources")({
    validateSearch: zod.object({
        onboardingJourneyId: uuidZodValidator,
        connectionAttemptId: uuidZodValidator.optional(),
    }),
    beforeLoad: async ({context, search}) => {
        const {onboardingJourneyId, connectionAttemptId} = search;
        throwRedirectToConfigConnectDataSourcesWhenPresent({
            workspaceId: context.workspace.id,
            onboardingJourneyId: onboardingJourneyId,
            connectionAttemptId: connectionAttemptId,
        });
        return {
            onboardingJourneyId: onboardingJourneyId,
            connectionAttemptId: connectionAttemptId,
        };
    },
    loader: async ({context}) => {
        const onboardingJourneyResult = jsonZodValidator
            .pipe(onboardingJourneyZodValidator)
            .safeParse(localStorage.getItem(`onboardingJourney/${context.onboardingJourneyId}`));
        if (onboardingJourneyResult.success !== true) {
            console.log(onboardingJourneyResult.success);
            throw redirect({
                to: "/u",
            });
        }
        const onboardingJourney = onboardingJourneyResult.data;

        // #region 1b26fa63-8038-4bb1-9296-8349811a690d
        // TODO: Ideally this should not be required over here, since we can directly
        // fetch this in the company selector dropdown lazily
        const accessibleWorkspacesResult = await getAccessibleWorkspacesWithCaching({
            accessToken: context.authenticationStatus.accessToken,
            queryClient: context.queryClient,
            intellsysBaseUrl: context.intellsysBaseUrl,
        });
        if (accessibleWorkspacesResult.success === false) {
            throw accessibleWorkspacesResult.error;
        }
        const accessibleWorkspaces = accessibleWorkspacesResult.data;

        const workspace = accessibleWorkspaces.find((workspace) => workspace.id === onboardingJourney.workspaceId);
        if (workspace === undefined) {
            // Ideally this should never happen
            console.log("Workspace not found; 60db1ecb-1673-479b-9795-883b995d6ed7");
            throw redirect({
                to: "/u",
            });
        }

        const dataSourcesResult = await getDataSourcesForFrontend({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: workspace.id,
        });
        if (dataSourcesResult.success !== true) {
            throw dataSourcesResult.error;
        }
        const dataSources = dataSourcesResult.data;

        const totalDataSourceInstancesCount = dataSources.reduce((acc, dataSource) => acc + dataSource.dataSourceInstances.length, 0);
        if (totalDataSourceInstancesCount > 0) {
            console.log("Workspace has data sources, No need of onboarding");
            throw redirect({
                to: "/w/$workspaceId/ottosync/data-sources",
                params: {
                    workspaceId: workspace.id,
                },
            });
        }
        // #endregion

        // #region Refresh connection attempts from local storage
        context.connectionAttemptClient.refreshFromLocalStorage();
        // #endregion

        if (onboardingJourney.currentStep === OnboardingJourneyStep.connectDataSources) {
            const allConnectionAttempts = context.connectionAttemptClient.getAllConnectionAttempts();
            for (const connectionAttempt of allConnectionAttempts) {
                if (onboardingJourney.currentStepInput.connectionAttemptIds.includes(connectionAttempt.id) === true) {
                    if (connectionAttempt.status !== DAG_ConnectionAttemptStatus.inProgress) {
                        continue;
                    }
                    const dataSource = dataSources.find((dataSource) => dataSource.id === connectionAttempt.dataSourceId);
                    if (dataSource === undefined) {
                        throw new Error("c3256870-18f5-40bb-b3a3-b64d16220664");
                    }
                    const dataSourceConnectionStrategyDetails = dataSourceConnectionStrategyDetailsMap[dataSource.connectionStrategy];

                    const currentStep = dataSourceConnectionStrategyDetails.connectionSteps.find((step) => step.id === connectionAttempt.currentStepId);
                    if (currentStep === undefined) {
                        throw new Error("371d404e-664a-4a9b-95b2-94e594dd25af");
                    }
                    if (currentStep.isResumable === false) {
                        const connectionAttemptUpdateResult = context.connectionAttemptClient.setConnectionAttempt({
                            ...connectionAttempt,
                            updatedAt: getCurrentIsoTimestamp(),
                            revisionId: generateUuid(),
                            status: DAG_ConnectionAttemptStatus.failed,
                        });
                        if (connectionAttemptUpdateResult.success !== true) {
                            throw connectionAttemptUpdateResult.error;
                        }
                    }
                }
            }
        }

        return {
            workspace: workspace,
            dataSources: dataSources,
            initialOnboardingJourney: onboardingJourney,
            connectionAttemptId: context.connectionAttemptId,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
